import type {
  NotificationChannelView,
  OfflinePolicy,
  PluginParam,
  PluginView,
  ScheduledTaskRunView,
  ScheduledTaskScopeType,
  ScheduledTaskStatus,
  ScheduledTaskView,
  ScheduleKind,
  ScheduleSpec,
} from '@occ/contracts';
import { useCan } from '@refinedev/core';
import { CalendarClock, History, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeHttpError } from '../../api/http';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  Modal,
  Select,
  Toasts,
  useToasts,
} from '../../components/ui';
import { useHierarchy } from '../../hierarchy/HierarchyProvider';
import { listNotificationChannels } from '../../data/notification-channels';
import { listPlugins } from '../../data/plugins';
import {
  createScheduledTask,
  deleteScheduledTask,
  listScheduledTaskRuns,
  listScheduledTasks,
  runScheduledTaskNow,
  updateScheduledTask,
} from '../../data/scheduled-tasks';

/**
 * Programador de tareas (M14): dispara un plugin ya existente sobre un
 * cliente, una sucursal o un nodo, en un horario — sin que un humano tenga
 * que ejecutarlo a mano. Ver `docs/tech-specs/M14-programador-de-tareas.md`.
 *
 * Fuera del catálogo de acciones, mismo criterio que reglas de alerta y
 * plugins: crear o disparar una tarea es administración de flota, no algo
 * que un nodo ejecute.
 */

const DAY_LABEL = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const STATUS_TONE: Record<ScheduledTaskStatus, 'neutral' | 'ok' | 'warn'> = {
  draft: 'neutral',
  active: 'ok',
  paused: 'warn',
};

const RUN_STATUS_TONE: Record<ScheduledTaskRunView['status'], 'neutral' | 'ok' | 'warn'> = {
  pending: 'neutral',
  running: 'neutral',
  completed: 'ok',
  partial: 'warn',
};

function describeSchedule(spec: ScheduleSpec): string {
  switch (spec.kind) {
    case 'interval':
      return `Cada ${spec.everyMinutes} min`;
    case 'weekly': {
      const days = [...spec.daysOfWeek].sort().map((day) => DAY_LABEL[day]).join('/');
      const hh = String(spec.hour).padStart(2, '0');
      const mm = String(spec.minute).padStart(2, '0');
      return `${days} · ${hh}:${mm} UTC`;
    }
    case 'once':
      return `Una vez: ${new Date(spec.runAt).toLocaleString()}`;
  }
}

function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

type TaskForm = {
  id: string | null;
  pluginId: string;
  scopeType: ScheduledTaskScopeType;
  scopeId: string;
  paramsOverrides: Record<string, string>;
  scheduleKind: ScheduleKind;
  everyMinutes: number;
  daysOfWeek: number[];
  hour: number;
  minute: number;
  runAt: string;
  status: ScheduledTaskStatus;
  offlinePolicy: OfflinePolicy;
  retryWindowHours: number;
  staggerSeconds: number;
  channelIds: string[];
};

const formOf = (task?: ScheduledTaskView): TaskForm => {
  const spec = task?.scheduleSpec;
  const inHour = new Date(Date.now() + 60 * 60 * 1_000);
  return {
    id: task?.id ?? null,
    pluginId: task?.pluginId ?? '',
    scopeType: task?.scopeType ?? 'branch',
    scopeId: task?.scopeId ?? '',
    paramsOverrides: task?.paramsOverrides ?? {},
    scheduleKind: spec?.kind ?? 'interval',
    everyMinutes: spec?.kind === 'interval' ? spec.everyMinutes : 60,
    daysOfWeek: spec?.kind === 'weekly' ? spec.daysOfWeek : [1],
    hour: spec?.kind === 'weekly' ? spec.hour : 3,
    minute: spec?.kind === 'weekly' ? spec.minute : 0,
    runAt: toDatetimeLocal(spec?.kind === 'once' ? new Date(spec.runAt) : inHour),
    status: task?.status ?? 'draft',
    offlinePolicy: task?.offlinePolicy ?? 'retry_on_reconnect',
    retryWindowHours: task?.retryWindowHours ?? 24,
    staggerSeconds: task?.staggerSeconds ?? 30,
    channelIds: task?.channelIds ?? [],
  };
};

const buildScheduleSpec = (form: TaskForm): ScheduleSpec => {
  switch (form.scheduleKind) {
    case 'interval':
      return { kind: 'interval', everyMinutes: form.everyMinutes };
    case 'weekly':
      return { kind: 'weekly', daysOfWeek: form.daysOfWeek, hour: form.hour, minute: form.minute };
    case 'once':
      return { kind: 'once', runAt: new Date(form.runAt).toISOString() };
  }
};

const canSubmit = (form: TaskForm): boolean =>
  form.pluginId !== '' &&
  form.scopeId !== '' &&
  (form.scheduleKind !== 'weekly' || form.daysOfWeek.length > 0);

export function ScheduledTasksPage() {
  const { data: canEdit } = useCan({ resource: 'scheduled-tasks', action: 'edit' });
  const { tree } = useHierarchy();

  const [tasks, setTasks] = useState<ScheduledTaskView[]>([]);
  const [plugins, setPlugins] = useState<PluginView[]>([]);
  const [channels, setChannels] = useState<NotificationChannelView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [form, setForm] = useState<TaskForm | null>(null);
  const [deleting, setDeleting] = useState<ScheduledTaskView | null>(null);
  const [viewingRuns, setViewingRuns] = useState<ScheduledTaskView | null>(null);
  const [busy, setBusy] = useState(false);

  const toasts = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextTasks, nextPlugins, nextChannels] = await Promise.all([
        listScheduledTasks(),
        listPlugins(),
        listNotificationChannels(),
      ]);
      setTasks(nextTasks);
      setPlugins(nextPlugins);
      setChannels(nextChannels);
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pluginById = useMemo(() => new Map(plugins.map((plugin) => [plugin.id, plugin])), [plugins]);

  const scopeOptions = useMemo(() => {
    const clients = tree?.clients ?? [];
    return {
      client: clients.map((client) => ({ id: client.id, label: client.name })),
      branch: clients.flatMap((client) =>
        client.branches.map((branch) => ({ id: branch.id, label: `${client.name} · ${branch.name}` })),
      ),
      node: clients.flatMap((client) =>
        client.branches.flatMap((branch) =>
          branch.nodes.map((node) => ({
            id: node.id,
            label: `${client.name} · ${branch.name} · ${node.alias}`,
          })),
        ),
      ),
    };
  }, [tree]);

  const submit = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const base = {
        pluginId: form.pluginId,
        scopeType: form.scopeType,
        scopeId: form.scopeId,
        paramsOverrides: form.paramsOverrides,
        scheduleSpec: buildScheduleSpec(form),
        offlinePolicy: form.offlinePolicy,
        retryWindowHours: form.retryWindowHours,
        staggerSeconds: form.staggerSeconds,
        channelIds: form.channelIds,
      };
      if (form.id) {
        await updateScheduledTask(form.id, { ...base, status: form.status });
      } else {
        await createScheduledTask({ ...base, status: form.status });
      }
      setForm(null);
      toasts.ok(form.id ? 'Tarea actualizada.' : 'Tarea creada.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteScheduledTask(deleting.id);
      setDeleting(null);
      toasts.ok('Tarea borrada.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (task: ScheduledTaskView) => {
    const next = task.status === 'active' ? 'paused' : 'active';
    try {
      await updateScheduledTask(task.id, { status: next });
      toasts.ok(next === 'active' ? 'Tarea activada.' : 'Tarea pausada.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    }
  };

  const runNow = async (task: ScheduledTaskView) => {
    try {
      await runScheduledTaskNow(task.id);
      toasts.ok('Disparada — el reparto sigue en segundo plano.');
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    }
  };

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Programador de tareas</h1>
          <p className="text-sm opacity-60">
            Dispara un plugin ya existente sobre un cliente, una sucursal o un nodo, en un horario —
            con reintento automático si el nodo estaba apagado.
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!canEdit?.can}
          title={canEdit?.can ? undefined : canEdit?.reason}
          onClick={() => setForm(formOf())}
        >
          <Plus className="size-4" />
          Nueva tarea
        </Button>
      </div>

      {!canEdit?.can && (
        <Alert>{canEdit?.reason ?? 'Solo un administrador del tenant administra tareas programadas.'}</Alert>
      )}

      {error !== null && <ErrorNotice error={error} what="Cargar las tareas programadas" />}

      {loading && tasks.length === 0 ? (
        <Loading label="Cargando tareas…" />
      ) : plugins.length === 0 ? (
        <Card>
          <EmptyState title="Ningún plugin todavía">
            El programador dispara plugins ya existentes — créalos primero en Administración →
            Plugins.
          </EmptyState>
        </Card>
      ) : tasks.length === 0 ? (
        <Card>
          <EmptyState title="Ninguna tarea programada todavía">
            Una tarea programada corre un plugin sobre un cliente, sucursal o nodo, en un horario,
            sin que nadie tenga que dispararla a mano.
          </EmptyState>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tasks.map((task) => {
            const plugin = pluginById.get(task.pluginId);
            return (
              <Card key={task.id} className="flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="inline-flex items-center gap-1.5 font-semibold tracking-tight">
                      <CalendarClock className="size-4 opacity-70" />
                      {plugin?.name ?? '(plugin borrado)'}
                    </h2>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge tone={STATUS_TONE[task.status]}>{task.status}</Badge>
                      <Badge>{task.scopeType}</Badge>
                      <Badge>{describeSchedule(task.scheduleSpec)}</Badge>
                      {task.offlinePolicy === 'retry_on_reconnect' && (
                        <Badge>reintenta al reconectar</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <p className="mt-2 text-xs opacity-60">
                  {task.nextRunAt
                    ? `Próxima vez: ${new Date(task.nextRunAt).toLocaleString()}`
                    : 'Sin próxima vez (pausada o ya disparada).'}
                </p>

                <div className="mt-3 flex justify-end gap-1 border-t border-black/5 pt-2 dark:border-white/5">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Ver corridas"
                    aria-label="Ver corridas"
                    onClick={() => setViewingRuns(task)}
                  >
                    <History className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Disparar ahora"
                    aria-label="Disparar ahora"
                    disabled={!canEdit?.can}
                    onClick={() => void runNow(task)}
                  >
                    <Play className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canEdit?.can || task.status === 'draft'}
                    onClick={() => void toggleStatus(task)}
                  >
                    {task.status === 'active' ? 'Pausar' : 'Activar'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Editar"
                    aria-label="Editar"
                    disabled={!canEdit?.can}
                    onClick={() => setForm(formOf(task))}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Borrar"
                    aria-label="Borrar"
                    disabled={!canEdit?.can}
                    className="hover:text-destructive"
                    onClick={() => setDeleting(task)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {form && (
        <TaskFormModal
          form={form}
          busy={busy}
          plugins={plugins}
          channels={channels}
          scopeOptions={scopeOptions}
          onChange={setForm}
          onCancel={() => setForm(null)}
          onSubmit={() => void submit()}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Borrar tarea programada"
        confirmLabel="Borrar"
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void remove()}
      >
        <p>
          Se borra la tarea sobre <strong>{deleting ? pluginById.get(deleting.pluginId)?.name : ''}</strong>.
          El historial de corridas ya hechas no se toca.
        </p>
      </ConfirmDialog>

      {viewingRuns && (
        <RunsModal task={viewingRuns} plugin={pluginById.get(viewingRuns.pluginId)} onClose={() => setViewingRuns(null)} />
      )}
    </section>
  );
}

function TaskFormModal({
  form,
  busy,
  plugins,
  channels,
  scopeOptions,
  onChange,
  onCancel,
  onSubmit,
}: {
  form: TaskForm;
  busy: boolean;
  plugins: PluginView[];
  channels: NotificationChannelView[];
  scopeOptions: { client: { id: string; label: string }[]; branch: { id: string; label: string }[]; node: { id: string; label: string }[] };
  onChange: (form: TaskForm) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const patch = (fields: Partial<TaskForm>) => onChange({ ...form, ...fields });
  const isNew = form.id === null;
  const selectedPlugin = plugins.find((plugin) => plugin.id === form.pluginId) ?? null;
  const options = scopeOptions[form.scopeType];

  const toggleDay = (day: number) => {
    const next = form.daysOfWeek.includes(day)
      ? form.daysOfWeek.filter((d) => d !== day)
      : [...form.daysOfWeek, day];
    patch({ daysOfWeek: next });
  };

  const toggleChannel = (id: string) => {
    const next = form.channelIds.includes(id)
      ? form.channelIds.filter((c) => c !== id)
      : [...form.channelIds, id];
    patch({ channelIds: next });
  };

  return (
    <Modal
      open
      wide
      title={isNew ? 'Nueva tarea programada' : 'Editar tarea programada'}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={busy || !canSubmit(form)}>
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Plugin"
            value={form.pluginId}
            onChange={(event) => patch({ pluginId: event.target.value })}
          >
            <option value="">Elegir…</option>
            {plugins.map((plugin) => (
              <option key={plugin.id} value={plugin.id}>
                {plugin.name} (v{plugin.version})
              </option>
            ))}
          </Select>
          <Select
            label="Estado"
            value={form.status}
            onChange={(event) => patch({ status: event.target.value as ScheduledTaskStatus })}
          >
            <option value="draft">Borrador (no dispara)</option>
            <option value="active">Activa</option>
            <option value="paused">Pausada</option>
          </Select>
        </div>

        {selectedPlugin && selectedPlugin.paramsSchema.length > 0 && (
          <div className="grid gap-2 rounded bg-accent/20 p-3 sm:grid-cols-2">
            {selectedPlugin.paramsSchema.map((param) => (
              <ParamField
                key={param.name}
                param={param}
                value={form.paramsOverrides[param.name] ?? param.default ?? ''}
                onChange={(value) =>
                  patch({ paramsOverrides: { ...form.paramsOverrides, [param.name]: value } })
                }
              />
            ))}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Ámbito"
            value={form.scopeType}
            onChange={(event) =>
              patch({ scopeType: event.target.value as ScheduledTaskScopeType, scopeId: '' })
            }
          >
            <option value="client">Cliente entero</option>
            <option value="branch">Una sucursal</option>
            <option value="node">Un nodo</option>
          </Select>
          <Select
            label={form.scopeType === 'client' ? 'Cliente' : form.scopeType === 'branch' ? 'Sucursal' : 'Nodo'}
            value={form.scopeId}
            onChange={(event) => patch({ scopeId: event.target.value })}
          >
            <option value="">Elegir…</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <section className="grid gap-2 rounded bg-accent/20 p-3">
          <Select
            label="Horario"
            value={form.scheduleKind}
            onChange={(event) => patch({ scheduleKind: event.target.value as ScheduleKind })}
          >
            <option value="interval">Cada N minutos</option>
            <option value="weekly">Días de la semana, a una hora</option>
            <option value="once">Una sola vez</option>
          </Select>

          {form.scheduleKind === 'interval' && (
            <Field
              label="Cada cuántos minutos"
              type="number"
              min={15}
              max={43_200}
              value={form.everyMinutes}
              onChange={(event) => patch({ everyMinutes: Number(event.target.value) || 60 })}
            />
          )}

          {form.scheduleKind === 'weekly' && (
            <>
              <div className="flex flex-wrap gap-1.5">
                {DAY_LABEL.map((label, day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`rounded px-2 py-1 text-xs transition ${
                      form.daysOfWeek.includes(day)
                        ? 'bg-[var(--occ-brand)] text-white'
                        : 'border border-black/10 dark:border-white/15 opacity-70 hover:opacity-100'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Hora (UTC)"
                  type="number"
                  min={0}
                  max={23}
                  value={form.hour}
                  onChange={(event) => patch({ hour: Number(event.target.value) || 0 })}
                />
                <Field
                  label="Minuto"
                  type="number"
                  min={0}
                  max={59}
                  value={form.minute}
                  onChange={(event) => patch({ minute: Number(event.target.value) || 0 })}
                />
              </div>
            </>
          )}

          {form.scheduleKind === 'once' && (
            <Field
              label="Fecha y hora (tu huso horario)"
              type="datetime-local"
              value={form.runAt}
              onChange={(event) => patch({ runAt: event.target.value })}
            />
          )}
        </section>

        <section className="grid gap-2 rounded bg-accent/20 p-3">
          <Select
            label="Si el nodo está apagado a esa hora"
            value={form.offlinePolicy}
            onChange={(event) => patch({ offlinePolicy: event.target.value as OfflinePolicy })}
          >
            <option value="retry_on_reconnect">Reintentar cuando vuelva a conectarse</option>
            <option value="skip">Saltarlo, no reintentar</option>
          </Select>
          {form.offlinePolicy === 'retry_on_reconnect' && (
            <Field
              label="Ventana de reintento (horas)"
              type="number"
              min={1}
              max={168}
              value={form.retryWindowHours}
              onChange={(event) => patch({ retryWindowHours: Number(event.target.value) || 24 })}
            />
          )}
          <Field
            label="Pausa entre nodos (segundos)"
            hint="Evita abrir cientos de ejecuciones en el mismo segundo contra el broker."
            type="number"
            min={0}
            max={3_600}
            value={form.staggerSeconds}
            onChange={(event) => patch({ staggerSeconds: Number(event.target.value) || 0 })}
          />
        </section>

        <section className="grid gap-1.5">
          <span className="text-sm opacity-70">Avisar el resultado por</span>
          {channels.length === 0 ? (
            <p className="text-xs opacity-60">
              No hay canales configurados — créalos en Administración → Notificaciones.
            </p>
          ) : (
            channels.map((channel) => (
              <Checkbox
                key={channel.id}
                label={`${channel.name} (${channel.kind})`}
                checked={form.channelIds.includes(channel.id)}
                onChange={() => toggleChannel(channel.id)}
              />
            ))
          )}
        </section>
      </div>
    </Modal>
  );
}

function ParamField({
  param,
  value,
  onChange,
}: {
  param: PluginParam;
  value: string;
  onChange: (value: string) => void;
}) {
  const label = (param.label ?? param.name) + (param.required ? ' *' : '');
  if (param.type === 'boolean') {
    return (
      <Select label={label} value={value || 'false'} onChange={(event) => onChange(event.target.value)}>
        <option value="true">true</option>
        <option value="false">false</option>
      </Select>
    );
  }
  return (
    <Field
      label={label}
      type={param.type === 'number' ? 'number' : 'text'}
      value={value}
      placeholder={param.default}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function RunsModal({
  task,
  plugin,
  onClose,
}: {
  task: ScheduledTaskView;
  plugin: PluginView | undefined;
  onClose: () => void;
}) {
  const [runs, setRuns] = useState<ScheduledTaskRunView[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    listScheduledTaskRuns(task.id)
      .then((rows) => {
        if (!cancelled) setRuns(rows);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause);
      });
    return () => {
      cancelled = true;
    };
  }, [task.id]);

  return (
    <Modal open title={`Corridas — ${plugin?.name ?? task.id}`} onClose={onClose}>
      {error !== null && <ErrorNotice error={error} what="Cargar las corridas" />}
      {runs === null && error === null ? (
        <Loading label="Cargando corridas…" />
      ) : runs && runs.length === 0 ? (
        <EmptyState title="Sin corridas todavía" />
      ) : (
        <div className="grid gap-2">
          {runs?.map((run) => (
            <div key={run.id} className="grid gap-1 rounded bg-accent/20 p-2.5 text-sm">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone={RUN_STATUS_TONE[run.status]}>{run.status}</Badge>
                <Badge>{run.triggerKind === 'manual' ? 'manual' : 'programada'}</Badge>
                <span className="opacity-60">{new Date(run.startedAt).toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs opacity-70">
                <span>{run.nodesTotal} nodos</span>
                <span>{run.nodesSucceeded} ok</span>
                {run.nodesFailed > 0 && <span>{run.nodesFailed} fallidos</span>}
                {run.nodesSkippedOffline > 0 && <span>{run.nodesSkippedOffline} sin conexión</span>}
                {run.nodesAwaitingReconnect > 0 && (
                  <span>{run.nodesAwaitingReconnect} esperando reconexión</span>
                )}
                {run.nodesExpired > 0 && <span>{run.nodesExpired} sin responder</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
